import _ from 'lodash';
import React from 'react';
import { HashRouter, Route } from 'react-router-dom';

import FacilityActionsPanel from './FacilityActionsPanel';
import SettingsAlertsPanel from './SettingsAlertsPanel';
import SettingsBillingPanel from './SettingsBillingPanel';
import SettingsFeesPanel from './SettingsFeesPanel';
import SettingsGeneralPanel from './SettingsGeneralPanel';
import SettingsPaymentApplicationsPanel from './SettingsPaymentApplicationPanel';
import SettingsTerminationPanel from './SettingsTerminationPanel';
import SettingsTermsPanel from './SettingsTermsPanel';

import { getBillingDashboardPageBreadcrumb } from '../Billing/BillingDashboardPage';

import PageNavigationLink from '../../PageNavigationLink';
import PageNavigationLinks from '../../PageNavigationLinks';

import ActionButton from '../../Common/ActionButton';
import NavigationBreadcrumbs from '../../Common/NavigationBreadcrumbs';
import PageHeader from '../../Common/PageHeader';
import ProcessingIndicator from '../../Common/ProcessingIndicator';

import Field from '../../Fields/Field';
import InputSelectField from '../../Fields/InputSelectField';

import {
    queryCreateFacilitySettings,
    queryGetFacilitySettings,
    queryGetFacilityPaymentTypes,
    mutateUpdateFacilityPaymentTypes,
    queryListFacilities,
    queryUpdateFacilitySettings
} from '../../../Queries';
import {
    getOrganizationInvoiceNotesByFacilityUid,
} from '../../../Queries/Organization';


import AccessDevice from '../../../Entities/AccessDevice';
import AlertConfiguration from '../../../Entities/AlertConfiguration';
import {
    Facility,
    FacilitySettings,
    monthCloseDayLabels
} from '../../../Entities/Facility';
import PaymentApplicationMonth from '../../../Entities/PaymentApplicationMonth';

import {
    facilityQueryFields,
    getFacility
} from '../../../Utils/facility';

import '../Page.scss';
import './FacilityGeneralSettings.scss';

const facilitySettingsQueryFields = [
    'facility_id',
    'display',
    'require_car_info',
    'decline_recurring',
    'parking_period',
    'billing_timing',
    'billing_day',
    'payment_due_day',
    'first_month_proration',
    'first_month_proration_day',
    'display_organization_invoice_notes',
    'invoice_notes',
    'access_device_message',
    'lost_device_request',
    'mail_invoice_option',
    'termination_notice_day',
    'provide_survey',
    'terms_and_conditions',
    'terms_and_conditions_summary',
    'next_steps',
    'use_ach',
    'prior_month_application',
    'future_month_application',
    'admin_process_payments',
    'manage_recurring_payments',
    'edit_payment_options',
    'month_close_day',
    'month_close_day_type',
    'validation_products',
    'accounting_method',
    'organization_id',
    {
        'fees': [
            'id',
            'type',
            'price',
            'tax_1',
            'tax_2',
            'tax_3',
            'tax_4',
            'gross_price',
            'start_date'
        ]
    },
    {
        'access_devices': [
            'id',
            'type',
            'required',
            'offered',
            {
                'fee': [
                    'id',
                    'type',
                    'price',
                    'tax_1',
                    'tax_2',
                    'tax_3',
                    'tax_4',
                    'gross_price',
                    'start_date'
                ]
            }
        ]
    },
    {
        'survey_questions': [
            'id',
            'type',
            'structure',
            'order'
        ]
    },
    {
        'alert_configurations': [
            'type',
            'emails'
        ]
    }
]

class FacilityGeneralSettings extends React.Component {
    state = {
        pageInitializationComplete: false,
        pageInitializationErrors: [],
        facilitySettingsSaveInProgress: false,
        facilitySettingsSaveErrors: [],
        newMode: true,
        editMode: false,
        facilities: null,
        facility: null,
        settings: null,
        originalFacilityPaymentTypes: [],
        facilityPaymentTypes: [],
        paymentTypesUpdated: false,
        organizationInvoiceNotes: null,
    };

    fieldConfiguration = {
        facilitySettings: null,
        accessDevice: null
    };

    editStore = null

    facilityId = null;


    constructor(props) {
        super(props);

        const urlSearchParams = new URLSearchParams(window.location.search);

        const facilityId = urlSearchParams.get('facility');
        if (facilityId) this.facilityId = facilityId;
    }

    componentDidMount() {
        const initializationTasks = [];

        if (this.facilityId) {
            initializationTasks.push(this.setPageFacility(this.facilityId));
        } else {
            initializationTasks.push(new Promise((resolve, reject) => {
                this.fetchFacilities().then(facilities => {
                    this.setState({
                        facilities
                    }, () => {
                        resolve();
                    });
                }, errors => {
                    reject(errors);
                });
            }));
        }

        initializationTasks.push(new Promise((resolve, reject) => {
            FacilitySettings.getFieldAllowedValues().then(values => {
                this.fieldConfiguration.facilitySettings = {
                    ...values,
                    month_close_day: monthCloseDayLabels
                };
                resolve();
            }, errors => {
                reject(errors);
            });
        }));

        initializationTasks.push(new Promise((resolve, reject) => {
            AccessDevice.getFieldAllowedValues().then(values => {
                this.fieldConfiguration.accessDevice = values;
                resolve();
            }, errors => {
                reject(errors);
            });
        }));

        initializationTasks.push(new Promise((resolve, reject) => {
            AlertConfiguration.getFieldAllowedValues(['type']).then(values => {
                this.fieldConfiguration.alertConfiguration = values;
                resolve();
            }, errors => {
                reject(errors);
            });
        }));

        initializationTasks.push(new Promise((resolve, reject) => {
            PaymentApplicationMonth.getFieldAllowedValues().then(values => {
                this.fieldConfiguration.paymentApplicationMonth = values;
                resolve();
            }, errors => {
                reject(errors);
            });
        }));

        Promise.all(initializationTasks).then(() => {
            this.setState({ pageInitializationComplete: true }, () => {
                if (!window.location.hash || window.location.hash === '#/') {
                    window.location.hash = '#/general';
                }
            });
        }, errors => {
            this.setState({
                pageInitializationComplete: true,
                pageInitializationErrors: errors.map(({ message }) => message)
            });
        });
    }


    fetchFacilities() {
        return new Promise((resolve, reject) => {
            queryListFacilities({}, facilityQueryFields).then(response => {
                if (response.errors) {
                    reject(response.errors);
                    return;
                }

                const facilities = response.data.listFacilities.map(facility => {
                    return Facility.createFromQueryValues(facility);
                });
                resolve(facilities);
            }, error => {
                reject([error]);
            });
        });
    }

    fetchFacilitySettings(facilityId) {
        return new Promise((resolve, reject) => {
            queryGetFacilitySettings({
                facility_id: facilityId
            }, facilitySettingsQueryFields).then(response => {
                if (response.errors) {
                    reject(response.errors);
                    return;
                }

                const facilitySettings = (response.data.getFacilitySettings !== null) ?
                    FacilitySettings.createFromQueryValues(response.data.getFacilitySettings) : null;
                resolve(facilitySettings);
            }, error => {
                reject([error]);
            });
        });
    }

    fetchFacilityPaymentTypes(facilityId) {
        return new Promise((resolve, reject) => {
            queryGetFacilityPaymentTypes({ facilityId }, [{ 'paymentTypes': ['id'] }])
                .then(response => {
                    let paymentTypeIds = []

                    if (
                        response.data.getFacilityPaymentTypes?.paymentTypes
                        && response.data.getFacilityPaymentTypes?.paymentTypes.length
                    ) {
                        paymentTypeIds = response.data.getFacilityPaymentTypes.paymentTypes.map(({ id }) => id);
                    }

                    resolve(paymentTypeIds)
                }, error => {
                    reject([error]);
                })
        });
    }

    updateFacilityPaymentTypes(facilityId, paymentTypes) {
        return new Promise((resolve, reject) => {
            mutateUpdateFacilityPaymentTypes({ facilityId, paymentTypes }, [{ 'paymentTypes': ['id'] }])
                .then(response => {
                    let paymentTypeIds = []

                    if (
                        response.data.updateFacilityPaymentTypes?.paymentTypes
                        && response.data.updateFacilityPaymentTypes?.paymentTypes.length
                    ) {
                        paymentTypeIds = response.data.updateFacilityPaymentTypes.paymentTypes.map(({ id }) => id);
                    }

                    resolve(paymentTypeIds)
                }, error => {
                    reject([error]);
                })
        })
    }

    fetchOrganizationInvoiceNotes(facilityId) {
        return new Promise((resolve, reject) => {
            getOrganizationInvoiceNotesByFacilityUid({ facility_uid: facilityId })
                .then(response => {
                    resolve(response.data.getOrganizationSettingsByFacilityUid.settings.invoice_notes)
                }, error => {
                    reject([error]);
                });
        });
    }

    setPageFacility(facilityId) {
        this.facilityId = facilityId;

        return new Promise((resolve, reject) => {
            Promise.all([
                getFacility(facilityId),
                this.fetchFacilitySettings(facilityId),
                this.fetchOrganizationInvoiceNotes(facilityId),
                this.fetchFacilityPaymentTypes(facilityId),
            ]).then(values => {
                let [facility, facilitySettings, organizationInvoiceNotes, facilityPaymentTypes] = values;

                const newMode = (facilitySettings === null);
                if (facilitySettings === null) facilitySettings = FacilitySettings.createWithDefaults(facilityId);
                this.setState({
                    facility,
                    settings: facilitySettings,
                    organizationInvoiceNotes,
                    originalFacilityPaymentTypes: [...facilityPaymentTypes],
                    facilityPaymentTypes,
                    newMode
                }, () => {
                    resolve();
                });
            }, errors => {
                reject(errors);
            });
        });
    }

    _onFacilityChange() {
        return value => {
            if (value === null) return;

            this.setState({
                pageInitializationComplete: false
            }, () => {
                this.setPageFacility(value).then(() => {
                    this.setState({
                        pageInitializationComplete: true
                    });
                }, errors => {
                    this.setState({
                        pageInitializationComplete: true,
                        pageInitializationErrors: errors.map(({ message }) => message)
                    });
                });
            });
        };
    }

    storePaymentApplicationSettingsData = (
        priorMonthApplication,
        futureMonthApplication,
        monthCloseDay,
        monthCloseDayType,
        adminProcessPayment,
        manageRecurringPayment,
        editPaymentOptions,
        accountingMethod
    ) => {
        const settings = _.cloneDeep(this.state.settings);
        if (priorMonthApplication === "MANUAL") {
            monthCloseDay = null;
        }
        else if (monthCloseDay === null) {
            monthCloseDay = "ONE";
        }

        settings.fields["prior_month_application"].value = priorMonthApplication;
        settings.fields["future_month_application"].value = futureMonthApplication;
        settings.fields["month_close_day"].value = monthCloseDay;
        settings.fields["month_close_day_type"].value = monthCloseDayType;
        settings.fields["admin_process_payments"].value = adminProcessPayment;
        settings.fields["manage_recurring_payments"].value = manageRecurringPayment;
        settings.fields["edit_payment_options"].value = editPaymentOptions;
        settings.fields["accounting_method"].value = accountingMethod;
        this.setState({ settings });
    }

    storeSettingsData = (inputId, value) => {
        const settings = _.cloneDeep(this.state.settings);

        switch (inputId) {
            case 'fees':
            case 'survey_questions':
            case 'validation_products':
                settings.fields[inputId] = value;
                break;
            default:
                settings.fields[inputId].value = value;
        }

        this.setState({ settings });
    }

    onUpdatePaymentTypes = (paymentTypes) => {
        this.setState({
            facilityPaymentTypes: paymentTypes,
            paymentTypesUpdated: true,
        })
    }


    handleEditButtonClick = () => {
        const { settings } = this.state;
        this.editStore = _.cloneDeep(settings);

        this.setState({
            editMode: true
        });
    }

    handleCancelButtonClick = () => {
        const settings = this.editStore;
        this.editStore = null;

        this.setState({
            editMode: false,
            settings,
            facilityPaymentTypes: this.state.originalFacilityPaymentTypes
        });
    }


    handleSaveButtonClick = () => {
        this.setState({
            facilitySettingsSaveInProgress: true
        })
        this.editStore = null;

        this.saveFacilitySettings(this.state.settings).then(facilitySettings => {
            this.setState({
                settings: facilitySettings,
                facilitySettingsSaveInProgress: false,
                editMode: false,
                newMode: false,
                facilitySettingsSaveErrors: [],
            });
        }, errors => {
            this.setState({
                facilitySettingsSaveInProgress: false,
                facilitySettingsSaveErrors: errors.map(({ message }) => message)
            });
        });

        if (this.state.paymentTypesUpdated) {
            this.updateFacilityPaymentTypes(this.facilityId, this.state.facilityPaymentTypes).then(updatedPaymentTypes => {
                this.setState({
                    paymentTypesUpdated: false,
                    facilityPaymentTypes: updatedPaymentTypes,
                    originalFacilityPaymentTypes: [...updatedPaymentTypes]
                })
            })
        }
    }


    saveFacilitySettings(facilitySettings) {
        return new Promise((resolve, reject) => {
            const {
                newMode
            } = this.state;
            const query = (newMode) ? queryCreateFacilitySettings : queryUpdateFacilitySettings;
            const variables = facilitySettings.getQueryFieldValues();

            query({
                facility_settings: variables
            }, facilitySettingsQueryFields).then(response => {
                if (response.errors) {
                    reject(response.errors);
                    return;
                }

                const responseKey = Object.keys(response.data)[0];
                const facilitySettings = FacilitySettings.createFromQueryValues(response.data[responseKey]);
                resolve(facilitySettings);
            }, error => {
                reject([error]);
            });
        });
    }


    renderNavigationBreadcrumbs() {
        const breadcrumbs = [];
        breadcrumbs.push(getBillingDashboardPageBreadcrumb(false, this.facilityId));
        breadcrumbs.push(getFacilityGeneralSettingsPageBreadcrumb());

        return (
            <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
        );
    }


    renderPageHeader() {
        const { facility } = this.state;

        const heading = 'General Settings';
        const subheading = (facility === null) ? null :
            facility.fields.facility_name.value;

        return (
            <PageHeader heading={heading}
                subheading={subheading} />
        );
    }


    renderPageInitializationMessage() {
        const {
            pageInitializationComplete,
            pageInitializationErrors
        } = this.state;

        if (!pageInitializationComplete) {
            return (
                <div className="parkchirp--page--user-message-container">
                    <ProcessingIndicator userMessage="Initializing, please wait..." />
                </div>
            );
        }

        if (pageInitializationErrors.length) {
            return (
                <div className="parkchirp--page--user-message-container">
                    <div className="parkchirp--page--user-message">
                        <p>The following errors occurred while initializing the page:</p>
                        <ul>
                            {pageInitializationErrors.map(error =>
                                <li key={performance.now()}>{error}</li>
                            )}
                        </ul>
                    </div>
                </div>
            );
        }
    }


    renderSidebar() {
        const {
            editMode,
            facilitySettingsSaveInProgress,
            facilitySettingsSaveErrors,
            settings
        } = this.state;

        return (
            <div className="parkchirp--sidebar-block">
                <div className="parkchirp--sidebar-block--page-actions">
                    {(settings !== null && !editMode) &&
                        <button type="button"
                            className="button button-primary button-large"
                            onClick={this.handleEditButtonClick}>
                            Edit
                        </button>
                    }
                    {(settings !== null && editMode) &&
                        <ActionButton label="Save"
                            inProgressState={facilitySettingsSaveInProgress}
                            onClick={this.handleSaveButtonClick}
                        />
                    }
                    {(settings !== null && editMode) &&
                        <ActionButton label="Cancel"
                            onClick={this.handleCancelButtonClick}
                        />
                    }
                </div>
                {(facilitySettingsSaveErrors.length > 0) &&
                    <div>
                        <p>The following errors occurred while saving the Facility Settings:</p>
                        <ul>
                            {facilitySettingsSaveErrors.map(error =>
                                <li key={performance.now()}>{error}</li>
                            )}
                        </ul>
                    </div>
                }
            </div>
        );
    }


    renderSelectFacilityForm() {
        const { facilities } = this.state;
        const options = facilities.map(facility => {
            return {
                value: facility.fields.uid.value,
                label: facility.fields.facility_name.value
            };
        });
        options.unshift({ value: null, label: '' });

        return (
            <Field label="Please select a Facility:"
                id="parkchirp--settings--select-facility"
                layout="stacked"
                containerClasses={['parkchirp--facility-general-settings--facility-field-container']}>
                <InputSelectField inputId="parkchirp--settings--select-facility"
                    value={null}
                    options={options}
                    onChange={this._onFacilityChange()}
                    editMode={true}
                />
            </Field>
        );
    }


    renderFacilitySettings() {
        const {
            editMode,
            settings,
            facility,
            organizationInvoiceNotes,
            facilityPaymentTypes,
        } = this.state;

        return (
            <React.Fragment>
                <PageNavigationLinks>
                    <PageNavigationLink path="/general" label="General" />
                    <PageNavigationLink path="/billing" label="Billing" />
                    {/* <PageNavigationLink path="/fees" label="Fees" /> */}
                    <PageNavigationLink path="/payment-application" label="Payment Application" />
                    <PageNavigationLink path="/termination" label="Termination Policy" />
                    <PageNavigationLink path="/terms" label="Terms &amp; Conditions" />
                    <PageNavigationLink path="/alerts" label="Alerts" />
                    <PageNavigationLink path="/actions" label="Actions" />
                </PageNavigationLinks>
                <Route path={["/", "/general"]} exact render={props => <SettingsGeneralPanel
                    {...props}
                    settings={settings}
                    organizationInvoiceNotes={organizationInvoiceNotes}
                    facility={facility}
                    fieldConfiguration={this.fieldConfiguration}
                    editMode={editMode}
                    onValueChange={this.storeSettingsData} />}
                />
                <Route path="/billing" render={props => <SettingsBillingPanel
                    {...props}
                    settings={settings}
                    fieldConfiguration={this.fieldConfiguration}
                    editMode={editMode}
                    onValueChange={this.storeSettingsData} />}
                />
                <Route path="/fees" render={props => <SettingsFeesPanel
                    {...props}
                    settings={settings}
                    fieldConfiguration={this.fieldConfiguration}
                    editMode={editMode}
                    onValueChange={this.storeSettingsData} />}
                />

                <Route
                    path="/payment-application"
                    render={props => <SettingsPaymentApplicationsPanel
                        {...props}
                        settings={settings}
                        facilityPaymentTypes={facilityPaymentTypes}
                        fieldConfiguration={this.fieldConfiguration}
                        editMode={editMode}
                        onValueChange={this.storeSettingsData}
                        storePaymentApplicationSettingsData={this.storePaymentApplicationSettingsData}
                        onUpdatePaymentTypes={this.onUpdatePaymentTypes}
                    />}
                />

                <Route path="/termination" render={props => <SettingsTerminationPanel
                    {...props}
                    settings={settings}
                    fieldConfiguration={this.fieldConfiguration}
                    editMode={editMode}
                    onValueChange={this.storeSettingsData} />}
                />
                <Route path="/terms" render={props => <SettingsTermsPanel
                    {...props}
                    settings={settings}
                    fieldConfiguration={this.fieldConfiguration}
                    editMode={editMode}
                    onValueChange={this.storeSettingsData} />}
                />
                <Route path="/alerts" render={props => <SettingsAlertsPanel
                    {...props}
                    settings={settings}
                    fieldConfiguration={this.fieldConfiguration}
                    editMode={editMode}
                    onValueChange={this.storeSettingsData} />}
                />
                <Route path="/actions" render={props => <FacilityActionsPanel
                    {...props}
                    facility={facility} />}
                />
            </React.Fragment>
        )
    }


    render() {
        const {
            pageInitializationComplete,
            pageInitializationErrors
        } = this.state;

        return (
            <div className="parkchirp--page parkchirp--page--general-settings">
                {this.renderNavigationBreadcrumbs()}
                {this.renderPageHeader()}
                <div className="parkchirp--page-content">
                    {(!pageInitializationComplete || pageInitializationErrors.length > 0) &&
                        this.renderPageInitializationMessage()
                    }
                    {(pageInitializationComplete && pageInitializationErrors.length === 0) &&
                        <div className="parkchirp--grid">
                            <div className="parkchirp--grid-column parkchirp--grid-column-size--main-content">
                                <HashRouter>
                                    <React.Fragment>
                                        {(this.facilityId === null) &&
                                            this.renderSelectFacilityForm()
                                        }
                                        {(this.facilityId !== null) &&
                                            this.renderFacilitySettings()
                                        }
                                    </React.Fragment>
                                </HashRouter>
                            </div>
                            <div className="parkchirp--grid-column parkchirp--grid-column-size--sidebar">
                                {this.renderSidebar()}
                            </div>
                        </div>
                    }
                </div>
            </div>
        );
    }
}
export default FacilityGeneralSettings;


export function getFacilityGeneralSettingsPageBreadcrumb(currentPage = true, facilityId = null) {
    const title = 'General Settings';
    const href = (currentPage) ? null :
        `?page=parkchirp_general_settings${(facilityId !== null) ? `&facility=${facilityId}` : ''}`;

    return { title, href };
}
